import type { FC } from 'react'
import { useState, useEffect } from 'react'
import cn from 'classnames'
import { ProductGrid } from 'modules/products/components'
import { FilterCheckList, FilterLink } from 'components/filter'
import { Pagination, Button } from 'components/ui'
import { Categories } from 'framework/categories'
import { CategoryService } from 'modules/categories/services'
import { TagService } from 'modules/tags/services'
import { withRouter } from 'next/router'
import { FILTER_DAYS } from 'modules/products/constants'
import { Tags } from 'framework/tags'
import { ROUTE_PRODUCT_INDEX } from 'routes/products'
import { MobileOnlyView, BrowserView } from 'react-device-detect'
import { Times, Filter } from 'components/icons'
import type { ProductListViewProps } from '.'
import s from './product-list-view.module.css'
import {useMercadinho} from "hooks/use-mercadinho";

function onlyUnique(value: string, index: number, self: any) {
  return self.indexOf(value) === index
}

const ProductListView: FC<ProductListViewProps> = ({
  className,
  products,
  lastPage = 1,
  baseUrl,
  router,
  sellersPage,
  isExp = false,
  isSearch = false,
}) => {
  const [categories, setCategories] = useState<Categories>(null as unknown as Categories)
  const [tags, setTags] = useState<Tags>(null as unknown as Tags)
  const [open, setOpen] = useState<boolean>(false)
  const [days, setDays] = useState<string[]>([])
  const [characteristics, setCharacteristics] = useState<string[]>([])
  const { isMercadinho } = useMercadinho()

  useEffect(() => {
    async function getCategories() {
      const categoryService = new CategoryService()
      const response = await categoryService.getCategories()
      setCategories(categoryService.parser(response.data.data))
    }

    async function getTags() {
      const tagService = new TagService()
      const response = await tagService.getTags()
      setTags(tagService.parser(response.data.data))
    }

    getCategories()
    getTags()
  }, [])

  function toggle() {
    setOpen(!open)
  }

  function handleDays(value: string) {
    const find = days.indexOf(value)
    if (find >= 0) {
      const x = days
      delete x[find]
      setDays([...x])
    } else {
      const x = [...days, value].filter(onlyUnique)
      setDays(x)
    }
  }

  function handleCharacteristics(value: string) {
    const find = characteristics.indexOf(value)
    if (find >= 0) {
      const x = characteristics
      delete x[find]
      setCharacteristics([...x])
    } else {
      const x = [...characteristics, value].filter(onlyUnique)
      setCharacteristics(x)
    }
  }

  const filter = () => {
    const currentPath = router.pathname
    const currentQuery = router.query

    if (days.length > 0) {
      currentQuery.days = days.join(',')
    }

    if (characteristics.length > 0) {
      currentQuery.tags = days.join(',')
    }

    setOpen(false)

    router.push({
      pathname: currentPath,
      query: currentQuery,
    })
  }

  return (
    <div className={cn(s.root, className)}>
      {!isMercadinho && (
        <>
          <MobileOnlyView>
            <div className={cn(s.filterMobile, open && s.filterMobileVisible)}>
              <span className={s.filterMobileTitle}>
                <span>Filtros</span>
                <Button variant="text" onClick={toggle}>
                  <Times />
                </Button>
              </span>

              <div>
                <div className={s.filterMobileRow}>
                  <FilterCheckList
                    title="Entrega"
                    param="days"
                    filters={FILTER_DAYS}
                    vertical
                    handle={handleDays}
                    checkedOptions={days}
                  />
                  <FilterCheckList
                    title="Características"
                    param="tags"
                    filters={tags}
                    vertical
                    handle={handleCharacteristics}
                    checkedOptions={characteristics}
                  />
                </div>
                <Button onClick={filter}>Filtrar</Button>
              </div>
              <br />
              <div>
                <FilterLink
                  filters={categories}
                  preserveQuery={false}
                  baseUrl={ROUTE_PRODUCT_INDEX}
                  title="Navegue nas categorias"
                />
              </div>
            </div>
          </MobileOnlyView>
          <BrowserView>
            <div className={cn(s.filters, open && s.filtersVisible)}>
              {!isExp && <FilterCheckList title="Entrega" param="days" filters={FILTER_DAYS} vertical />}
              <FilterLink filters={categories} preserveQuery={false} baseUrl={ROUTE_PRODUCT_INDEX} title="Categorias" />
              <FilterCheckList title="Características" param="tags" filters={tags} vertical />
            </div>
          </BrowserView>
        </>
      )}

      <main className={s.grid}>
        {products && products.length > 0 ? (
          <>
            <ProductGrid
              products={products}
              filters={
                !isMercadinho && (
                  <MobileOnlyView viewClassName={s.buttonFilter}>
                    <Button variant="ghost" onClick={toggle}>
                      <Filter />
                      &nbsp;&nbsp;Filtros
                    </Button>
                  </MobileOnlyView>
                )
              }
            />
            <Pagination total={lastPage} href={baseUrl} className={s.pagination} />
          </>
        ) : (
          <>
            <MobileOnlyView viewClassName={s.buttonFilter}>
              <Button variant="ghost" onClick={toggle}>
                <Filter />
                &nbsp;&nbsp;Filtros
              </Button>
            </MobileOnlyView>
            <div className={s.productNotFoundMobile}>
              {isSearch && !sellersPage
                ? 'Não encontramos nenhum produto de acordo com a sua busca, tente outro termo.'
                : ''}
              {isSearch === false && (
                <>
                  {sellersPage ? (
                    <>Seu CEP ainda não é atendido por este produtor da Fazendinha. Aguarde novidades!</>
                  ) : (
                    <>
                      Não encontramos nenhum produto nesta categoria para o seu CEP. Confira quais produtos estão
                      disponíveis{' '}
                      <Button href="/produtos" variant="link">
                        aqui
                      </Button>
                      .
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  )
}

export default withRouter(ProductListView)
